import './parity-pass1.css';
import './parity-pass2.css';
import './parity-pass3.css';
import './parity-pass4.css';
import './parity-pass5.css';
import './milestone3.css';
import './milestone5.css';
import './shell.css';
import './pwa/pwa.css';
import './pwa/iosInstallGuide.css';
import { inspectConceptArtAvailability } from './game/assetGate.js';
import { mountGameplay } from './game/gameplayScene.js';
import { applyGameplayArtPipeline } from './game/gameplayArtPipeline.js';
import {
  CAMPAIGN_LEVEL_NUMBERS,
  VISUAL_BENCHMARK_LEVEL_NUMBER,
  hasLevel,
  loadLevel,
  nextCampaignLevelNumber,
  normalizeLevelNumber,
} from './game/levelCatalog.js';
import { createProgressStore } from './game/progression.js';
import { createPwaManager } from './pwa/pwaManager.js';
import { mountPwaSettingsRow } from './pwa/pwaSettings.js';
import { mountIosInstallHint, openIosInstallGuide } from './pwa/iosInstallGuide.js';
import {
  applyShellSettings,
  createShellSettingsStore,
  renderHelp,
  renderHome,
  renderMap,
  renderSettings,
} from './game/gameShell.js';

const pwaManager = createPwaManager();
pwaManager.registerServiceWorker();
const progressStore = createProgressStore();
const shellSettingsStore = createShellSettingsStore();
applyShellSettings(shellSettingsStore.read());

function renderDevelopmentGate(result) {
  const app = document.querySelector('#app');
  const missing = result.missing.map((asset) => `<li><code>${asset.file}</code></li>`).join('');
  app.innerHTML = `<main class="bf-development-gate"><section class="bf-development-gate__panel"><p class="bf-development-gate__eyebrow">Development asset gate</p><h1>Bolt Forge</h1><p>Gameplay is locked because approved concept masters are missing. Placeholder gameplay is forbidden.</p><ul>${missing}</ul></section></main>`;
}

function retainedParams() {
  const current = new URLSearchParams(window.location.search);
  const next = new URLSearchParams();
  if (current.has('_deploy')) next.set('_deploy', current.get('_deploy'));
  return next;
}

function readRoute() {
  const params = new URLSearchParams(window.location.search);
  const debugReference = params.get('debug') === 'reference';
  const qaMode = params.get('qa') === '1';
  const requestedRaw = params.get('level');
  const progress = progressStore.read();

  if (requestedRaw != null || debugReference) {
    let requested = requestedRaw == null
      ? VISUAL_BENCHMARK_LEVEL_NUMBER
      : normalizeLevelNumber(requestedRaw, 1);

    if (!hasLevel(requested)) requested = 1;
    if (!qaMode && requested !== VISUAL_BENCHMARK_LEVEL_NUMBER && requested > progress.unlockedLevel) {
      requested = Math.min(progress.unlockedLevel, CAMPAIGN_LEVEL_NUMBERS.at(-1));
    }
    return { screen: 'game', requested, debugReference, qaMode };
  }

  const requestedScreen = params.get('screen') || 'home';
  const screen = ['home', 'map', 'help', 'settings'].includes(requestedScreen)
    ? requestedScreen
    : 'home';
  return { screen, requested: null, debugReference: false, qaMode: false };
}

function commitUrl(params, { replace = false } = {}) {
  const query = params.toString();
  const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
  if (replace) history.replaceState(null, '', url);
  else history.pushState(null, '', url);
}

function writeScreenRoute(screen, { replace = false } = {}) {
  const params = retainedParams();
  if (screen !== 'home') params.set('screen', screen);
  commitUrl(params, { replace });
}

function writeLevelRoute(levelNumber, {
  debugReference = false,
  qaMode = false,
  replace = false,
} = {}) {
  const params = retainedParams();
  params.set('level', String(levelNumber));
  if (debugReference) params.set('debug', 'reference');
  if (qaMode) params.set('qa', '1');
  commitUrl(params, { replace });
}

async function startLevel(levelNumber, routeOptions = {}) {
  const level = await loadLevel(levelNumber);
  const root = document.querySelector('#app');
  const nextNumber = nextCampaignLevelNumber(level.displayNumber);

  const controller = mountGameplay(root, level, {
    debugReference: routeOptions.debugReference === true && level.displayNumber === VISUAL_BENCHMARK_LEVEL_NUMBER,
    onComplete: (completedLevel, sessionState) => {
      if (routeOptions.qaMode || routeOptions.debugReference) return progressStore.read();
      if (!CAMPAIGN_LEVEL_NUMBERS.includes(completedLevel.displayNumber)) return null;
      return progressStore.completeLevel(
        completedLevel,
        sessionState,
        nextCampaignLevelNumber(completedLevel.displayNumber),
      );
    },
    onNextLevel: nextNumber
      ? () => navigateToLevel(nextNumber, { qaMode: routeOptions.qaMode })
      : null,
    onBackToMap: () => navigateToScreen('map'),
    onRestart: () => startLevel(level.displayNumber, routeOptions),
  });

  applyGameplayArtPipeline(controller.stage, level);
  controller.stage.dataset.qa = routeOptions.qaMode ? 'true' : 'false';

  if (routeOptions.debugReference && level.displayNumber === VISUAL_BENCHMARK_LEVEL_NUMBER) {
    controller.stage.classList.add('is-reference-debug');
  }

  if (routeOptions.qaMode || routeOptions.debugReference) {
    const badge = document.createElement('div');
    badge.className = 'bf-engine-badge';
    badge.textContent = level.displayNumber === VISUAL_BENCHMARK_LEVEL_NUMBER
      ? 'VISUAL BENCHMARK • ENGINE V1'
      : `LEVEL ${level.displayNumber} • ENGINE V1`;
    controller.stage.append(badge);
  }
  return controller;
}

function renderShellScreen(screen) {
  const root = document.querySelector('#app');
  const progress = progressStore.read();

  if (screen === 'map') {
    renderMap(root, {
      progress,
      onHome: () => navigateToScreen('home'),
      onSettings: () => navigateToScreen('settings'),
      onPlayLevel: (number) => navigateToLevel(number),
    });
    return;
  }

  if (screen === 'help') {
    renderHelp(root, {
      onHome: () => navigateToScreen('home'),
      onMap: () => navigateToScreen('map'),
    });
    return;
  }

  if (screen === 'settings') {
    const stage = renderSettings(root, {
      progress,
      settingsStore: shellSettingsStore,
      onHome: () => navigateToScreen('home'),
      onMap: () => navigateToScreen('map'),
      onResetProgress: () => {
        progressStore.reset();
        renderShellScreen('settings');
      },
    });
    mountPwaSettingsRow(stage, pwaManager, {
      onIosHowTo: () => openIosInstallGuide(stage, pwaManager),
    });
    return;
  }

  const stage = renderHome(root, {
    progress,
    onPlay: () => navigateToScreen('map'),
    onMap: () => navigateToScreen('map'),
    onHelp: () => navigateToScreen('help'),
    onSettings: () => navigateToScreen('settings'),
  });
  mountIosInstallHint(stage, pwaManager);
}

async function renderCurrentRoute({ replace = false } = {}) {
  const route = readRoute();

  if (route.screen === 'game') {
    if (replace) writeLevelRoute(route.requested, { ...route, replace: true });
    await startLevel(route.requested, route);
    return;
  }

  if (replace) writeScreenRoute(route.screen, { replace: true });
  renderShellScreen(route.screen);
}

function navigateToScreen(screen) {
  writeScreenRoute(screen);
  renderShellScreen(screen);
}

async function navigateToLevel(levelNumber, options = {}) {
  const progress = progressStore.read();
  const qaMode = options.qaMode === true;
  const normalized = normalizeLevelNumber(levelNumber, 1);
  const allowed = qaMode || normalized === VISUAL_BENCHMARK_LEVEL_NUMBER
    ? normalized
    : Math.min(normalized, Math.min(progress.unlockedLevel, CAMPAIGN_LEVEL_NUMBERS.at(-1)));
  writeLevelRoute(allowed, { qaMode, debugReference: options.debugReference === true });
  await startLevel(allowed, { qaMode, debugReference: options.debugReference === true });
}

async function boot() {
  const art = await inspectConceptArtAvailability();
  if (!art.ready) {
    renderDevelopmentGate(art);
    return;
  }
  await renderCurrentRoute({ replace: true });
}

window.addEventListener('popstate', () => {
  renderCurrentRoute().catch((error) => console.error(error));
});

boot().catch((error) => {
  console.error(error);
  document.querySelector('#app').innerHTML = `<main class="bf-development-gate"><section class="bf-development-gate__panel"><p class="bf-development-gate__eyebrow">Runtime error</p><h1>Bolt Forge</h1><p>${error.message}</p></section></main>`;
});
